import React, { ReactElement } from 'react';
import useDataPointsSearch from '@hooks/api/queries/useDataPointsSearch';
import useSubscription from '@hooks/useSubscription';
import { DataPointWithValue } from '@src/types/DataPoint';
import storeConnector from '@store/storeConnector';
import { SiteMeta } from '@src/types/SiteMeta';
import { DataPointQuery, UnitDataPoints, UnitConfig } from '@src/sld/types';
import TopSwitchResistorSection from '@src/sld/sagebrush/sagebrush_b59/components/TopSwitchResistorSection';
import Switch2Units from '@src/sld/sagebrush/sagebrush_b59/components/Switch2Units';
import Switch1Units from '@src/sld/sagebrush/sagebrush_b59/components/Switch1Units';
import Switch3Units from '@src/sld/sagebrush/sagebrush_b59/components/Switch3Units';
import MultiSwitchConnector from '@src/sld/components/MultiSwitchConnector/MultiSwitchConnector';
import BreakerStack1 from '@src/sld/sagebrush/sagebrush_b59/components/BreakerStack1';
import BreakerStack2 from '@src/sld/sagebrush/sagebrush_b59/components/BreakerStack2';

type SagebrushSLDProps = {
  siteMeta: SiteMeta;
};

const unitDataPoints: UnitDataPoints[] = [
  UnitDataPoints.KW,
  UnitDataPoints.KVAR,
  UnitDataPoints.SOC,
  UnitDataPoints.RacksOnline,
  UnitDataPoints.InvStatus,
  UnitDataPoints.RunMode,
];

// eslint-disable-next-line max-lines-per-function
function SageBrushB59Diagram(props: SagebrushSLDProps): ReactElement {
  const { siteMeta } = props;

  const queryItems: DataPointQuery[] = [];
  const units: UnitConfig[] = [];

  siteMeta.Units.forEach((controllerId: string): void => {
    unitDataPoints.forEach((pointName: UnitDataPoints): void => {
      queryItems.push({ category: 'unit', pointName, controllerId });
    });
  });

  const { data: dataPoints } = useDataPointsSearch('sagebrush_59-sld', [
    ...queryItems,
  ]);

  const dataPointsWithValue: DataPointWithValue[] = useSubscription(
    dataPoints || [],
  );

  const getUnitDataPoint = (
    pointName: string,
    unitPoints: DataPointWithValue[],
  ) =>
    unitPoints.find(
      (x: DataPointWithValue): boolean => x.pointName === pointName,
    );

  siteMeta.Units.forEach((controllerId: string, index: number): void => {
    const unitPoints: DataPointWithValue[] = dataPointsWithValue.filter(
      (dataPointObj: DataPointWithValue): boolean =>
        dataPointObj.controllerId === controllerId,
    );

    const kw = getUnitDataPoint(UnitDataPoints.KW, unitPoints)?.value;
    const kvar = getUnitDataPoint(UnitDataPoints.KVAR, unitPoints)?.value;
    const racksonline = getUnitDataPoint(
      UnitDataPoints.RacksOnline,
      unitPoints,
    )?.value;
    const invstatus = getUnitDataPoint(
      UnitDataPoints.InvStatus,
      unitPoints,
    )?.value;
    const soc = getUnitDataPoint(UnitDataPoints.SOC, unitPoints)?.value;
    const runmode = getUnitDataPoint(UnitDataPoints.RunMode, unitPoints)?.value;

    units.push({
      unitLabel: siteMeta.UnitNames[index],
      kw,
      kvar,
      racksonline,
      invstatus,
      soc,
      runmode,
    } as UnitConfig);
  });

  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      xlinkHref='http://www.w3.org/1999/xlink'
      viewBox='125 25 1300 645'
      xmlSpace='preserve'
      colorInterpolationFilters='sRGB'
      className='st10'
      style={{ fill: 'none', stroke: 'currentColor', overflowX: 'auto' }}
    >
      <title>Sagebrush B59</title>

      <TopSwitchResistorSection
        translateXCoordinate={-125}
        translateYCoordinate={-20}
      />
      <Switch1Units unitConfigs={units} />
      <Switch2Units unitConfigs={units} />
      <Switch3Units unitConfigs={units} />
      <MultiSwitchConnector
        translateXCoordinate={240}
        translateYCoordinate={-250}
        verticalLength={20}
        horizontalLength={260}
        startingXCoordinate={410}
      />
      <MultiSwitchConnector
        translateXCoordinate={75}
        translateYCoordinate={-392}
        verticalLength={20}
        horizontalLength={428}
        startingXCoordinate={285}
      />
      <BreakerStack1 />
      <BreakerStack2 />
    </svg>
  );
}

export default storeConnector(SageBrushB59Diagram, {
  config: ['siteMeta'],
});
